import { useEffect, useMemo, useState } from 'react'
import { Search, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { LoadError } from '@/components/campaigns/LoadError'
import { ExclusionChip, RecipientStatusChip } from '@/components/campaigns/StatusChip'
import { formatDateTime, formatNumber } from '@/components/campaigns/campaignFormat'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { useCampaignRecipientsQuery, useUpdateRecipients } from '@/queries'
import type { CampaignRecipient } from '@/services/crmCampaignsService'

const PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 300

interface RecipientsTableProps {
  campaignId: string
  /** Antes de lanzar se marcan y desmarcan; después solo se consulta qué ha pasado con cada uno. */
  mode: 'review' | 'monitor'
  /** Filtros que pone la pantalla: el estado y el gestor del panel. */
  status?: string
  agent?: string
}

// Lo que el sistema dejó fuera (baja, duplicado, sin consentimiento…) no se puede
// volver a meter a mano. Solo lo desmarcado a mano tiene vuelta.
const canSelect = (recipient: CampaignRecipient) =>
  recipient.incluido || recipient.motivo_exclusion === 'manual'

export function RecipientsTable({ campaignId, mode, status, agent }: RecipientsTableProps) {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<Map<string, CampaignRecipient>>(new Map())
  const [confirming, setConfirming] = useState(false)
  const update = useUpdateRecipients(campaignId)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(search.trim())
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => setPage(1), [status, agent])

  const recipients = useCampaignRecipientsQuery(campaignId, { page, q: query, estado: status, gestor: agent })
  const rows = useMemo(() => recipients.data?.results ?? [], [recipients.data])
  const total = recipients.data?.count ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const reviewing = mode === 'review'

  const selectableRows = rows.filter(canSelect)
  const allPageSelected = selectableRows.length > 0 && selectableRows.every((row) => selected.has(row.id))

  const setRowSelected = (recipient: CampaignRecipient, checked: boolean) =>
    setSelected((prev) => {
      const next = new Map(prev)
      if (checked) next.set(recipient.id, recipient)
      else next.delete(recipient.id)
      return next
    })

  const setPageSelected = (checked: boolean) =>
    setSelected((prev) => {
      const next = new Map(prev)
      for (const row of selectableRows) {
        if (checked) next.set(row.id, row)
        else next.delete(row.id)
      }
      return next
    })

  const selectedRows = [...selected.values()]
  const toRemove = selectedRows.filter((row) => row.incluido).map((row) => row.id)
  const toRestore = selectedRows.filter((row) => !row.incluido).map((row) => row.id)

  const applyChange = (ids: string[], included: boolean) => {
    update.mutate(
      { ids, included },
      {
        onSuccess: () => {
          toast.success(
            included
              ? t('campaigns.recipients.restored', { count: ids.length })
              : t('campaigns.recipients.removed', { count: ids.length }),
          )
          setSelected(new Map())
          setConfirming(false)
        },
        onError: (error) => {
          toast.error(error.message)
          setConfirming(false)
        },
      },
    )
  }

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-8"
          type="search"
          value={search}
          aria-label={t('campaigns.recipients.search')}
          placeholder={t('campaigns.recipients.search')}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {recipients.isPending ? (
        <div className="space-y-2" data-testid="recipients-loading">
          {[0, 1, 2, 3, 4].map((row) => (
            <Skeleton key={row} className="h-10 w-full" />
          ))}
        </div>
      ) : recipients.isError ? (
        <LoadError error={recipients.error} onRetry={() => void recipients.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Users className="h-6 w-6" />}
          title={query || status || agent ? t('campaigns.recipients.empty_filtered') : t('campaigns.recipients.empty_title')}
          description={query || status || agent ? undefined : t('campaigns.recipients.empty_description')}
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {reviewing ? (
                    <TableHead className="w-10">
                      <Checkbox
                        aria-label={t('campaigns.recipients.select_page')}
                        checked={allPageSelected}
                        disabled={selectableRows.length === 0}
                        onCheckedChange={(checked) => setPageSelected(checked === true)}
                      />
                    </TableHead>
                  ) : null}
                  <TableHead>{t('campaigns.recipients.col_name')}</TableHead>
                  <TableHead>{t('campaigns.recipients.col_phone')}</TableHead>
                  {reviewing ? (
                    <TableHead>{t('campaigns.recipients.col_reason')}</TableHead>
                  ) : (
                    <>
                      <TableHead>{t('campaigns.recipients.col_status')}</TableHead>
                      <TableHead>{t('campaigns.recipients.col_last_event')}</TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} className={cn(reviewing && !row.incluido && 'opacity-60')}>
                    {reviewing ? (
                      <TableCell>
                        <Checkbox
                          aria-label={t('campaigns.recipients.select_row', { name: row.nombre || row.telefono_e164 })}
                          checked={selected.has(row.id)}
                          disabled={!canSelect(row)}
                          onCheckedChange={(checked) => setRowSelected(row, checked === true)}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell className="font-medium">{row.nombre || t('campaigns.recipients.no_name')}</TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-xs">{row.telefono_e164}</TableCell>
                    {reviewing ? (
                      <TableCell>{row.incluido ? null : <ExclusionChip reason={row.motivo_exclusion} />}</TableCell>
                    ) : (
                      <>
                        <TableCell>
                          <RecipientStatusChip status={row.estado} />
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {row.motivo_parada || row.ultimo_evento || '—'}
                          {row.ultimo_evento_at ? ` · ${formatDateTime(row.ultimo_evento_at, lang)}` : ''}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              {t('campaigns.recipients.page_status', {
                page,
                pages: pageCount,
                total: formatNumber(total, lang),
              })}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="xs" disabled={page <= 1} onClick={() => setPage((prev) => prev - 1)}>
                {t('campaigns.common.previous')}
              </Button>
              <Button
                variant="outline"
                size="xs"
                disabled={page >= pageCount}
                onClick={() => setPage((prev) => prev + 1)}
              >
                {t('campaigns.common.next')}
              </Button>
            </div>
          </div>
        </>
      )}

      {reviewing && selected.size > 0 ? (
        <div
          role="toolbar"
          aria-label={t('campaigns.recipients.bulk_actions')}
          className="sticky bottom-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card p-2 shadow-sm"
        >
          <span className="px-2 text-sm">{t('campaigns.recipients.selected', { count: selected.size })}</span>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Map())}>
              {t('campaigns.recipients.clear_selection')}
            </Button>
            {toRestore.length > 0 ? (
              <Button variant="outline" size="sm" disabled={update.isPending} onClick={() => applyChange(toRestore, true)}>
                {t('campaigns.recipients.restore', { count: toRestore.length })}
              </Button>
            ) : null}
            {toRemove.length > 0 ? (
              <Button variant="destructive" size="sm" disabled={update.isPending} onClick={() => setConfirming(true)}>
                {t('campaigns.recipients.remove', { count: toRemove.length })}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('campaigns.recipients.confirm_title', { count: toRemove.length })}</AlertDialogTitle>
            <AlertDialogDescription>{t('campaigns.recipients.confirm_description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('campaigns.common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => applyChange(toRemove, false)}>
              {t('campaigns.recipients.remove', { count: toRemove.length })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
